import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as actions from 'containers/ProjectTools/Notes/actions';
import clsx from 'clsx';
import { isDesktop } from 'react-device-detect';

import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APDropdown from 'components/Dropdown/APDropdown';
import Drawer from 'components/Drawer';
import { MobileSm, DefaultSm } from 'components/MediaQueries';
import { NOTE_RESTRICTIONS } from 'utils/constants';
import APModal from 'components/Modal/APModal';
import InputField from 'components/FormFields/InputField';
import { getPreviewUrl, isUrlValid } from 'utils/general';
import FormSelect from 'components/FormFields/FormSelect';
import ImageUploadField from 'components/FormFields/ImageUploadField';
import { streamDownload } from 'api/notes';

export const NoteTemplateOptionsButton = ({
  id,
  url,
  imageUrls,
  image,
  originalImage,
  setImageUpdating,
  noteTemplate,
  renameAction,
}) => {
  const userInfo = useSelector((state) => state.global.userInfo);
  const { organization } = userInfo || {};
  const { id: orgId } = organization || {};

  const [opened, setOpened] = useState(false);
  const [target, setTarget] = useState(null);

  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [urlModalOpened, setUrlModalOpened] = useState(false);
  const [imageCropperOpened, setImageCropperOpen] = useState(false);
  const [renameModalOpened, setRenameModalOpened] = useState(false);

  const getRestrictionValue = () => {
    if(noteTemplate.clientReadOnly && noteTemplate.plannerOnly) {
      return 'None';
    } if (noteTemplate.clientReadOnly && !noteTemplate.plannerOnly) {
      return 'ClientEditingLocked';
    } if (!noteTemplate.clientReadOnly && noteTemplate.plannerOnly) {
      return 'VisibleToOrgMembersOnly';
    };
    return 'None';
  };

  const [restriction, setRestriction] = useState(getRestrictionValue());

  const [isPreparingDownload, setIsPreparingDownload] = useState(false);

  const [errorModalOpened, setErrorModalOpened] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState(null);
  const [errorModalTitle, setErrorModalTitle] = useState(null);
  const [errorModalButtonTitle, setErrorModalButtonTitle] = useState(null);

  const previewUrl = useMemo(() => getPreviewUrl(id, true), [id]);

  const dispatch = useDispatch();
  const {
    deleteNoteTemplate,
    updateNoteTemplate,
    duplicateNoteTemplate,
  } = bindActionCreators(actions, dispatch);

  const dismiss = () => {
    setOpened(false);
    target?.classList.add('hover-expose');
  };

  const errorModalCloseHandler = () => {
    setErrorModalOpened(false);
    setErrorModalMessage(null);
  };

  const updateTemplateUrl = (shortcutUrl) => {
    updateNoteTemplate({ id: noteTemplate.id, noteBody: { ...noteTemplate, url: shortcutUrl } });
  };

  const renameTemplate = (title) => {
    updateNoteTemplate({ id: noteTemplate.id, noteBody: { ...noteTemplate, title } });
  };

  const restrictionsNoteTemplate = (clientReadOnly, plannerOnly) => {
    updateNoteTemplate({ id: noteTemplate.id, noteBody: { ...noteTemplate, clientReadOnly, plannerOnly } });
  };

  const renderOptions = () => {
    const deleteItem = () => {
      setDeleteConfirmOpened(true);
      dismiss();
    };

    const restrictionOnChange = (e) => {
      setRestriction(e.currentTarget.value);
      switch (e.currentTarget.value) {
        case 'VisibleToOrgMembersOnly':
          restrictionsNoteTemplate(false, true);
          break;
        case 'ClientEditingLocked':
          restrictionsNoteTemplate(true, false);
          if (noteTemplate.isShortcut) {
            setErrorModalOpened(true);
            setErrorModalTitle('Heads up!');
            setErrorModalMessage(`Although client editing is locked for this shortcut in Aisle Planner, you may
              still need to manage outside permissions separately.`);
            setErrorModalButtonTitle('Got It!');
          }
          setOpened(false);
          break;
        case 'None':
          restrictionsNoteTemplate(false, false);
          break;
        default:
          break;
      };
    };

    const editCoverImage = (e) => {
      e.stopPropagation();
      setImageCropperOpen(true);
      dismiss();
    };

    const editURL = (e) => {
      e.stopPropagation();
      setUrlModalOpened(true);
      dismiss();
    };

    const renameItem = (e) => {
      e.stopPropagation();
      renameAction();
      dismiss();
    };

    const preview = (e)  => {
      e.stopPropagation();
      window.open(previewUrl, '_blank');
      dismiss();
    };

    const download = (e) => {
      e.stopPropagation();

      const params = { note_template_id: id };
      setIsPreparingDownload(true);
      const payload = {
        orgId,
        params,
      };

      streamDownload(payload)
        .then((response) => {
          if (response.status !== 401)
            window.location.replace(response.data.url);
          setIsPreparingDownload(false);
          dismiss();
        })
        .catch(() => {
          setIsPreparingDownload(false);
          setErrorModalOpened(true);
          setErrorModalTitle('Something went wrong.');
          setErrorModalMessage("Sorry, we couldn't prepare the download. Please try again.");
          setErrorModalButtonTitle('Close');
        });
    };

    const duplicateItem = () => {
      duplicateNoteTemplate({ id });
      dismiss();
    };

    const menuItems = [
      { title: 'Edit cover image', onClick: editCoverImage },
      ...(!url ? [{ title: 'Preview', onClick: preview }] : []),
      ...(url ? [{ title: 'Edit url', onClick: editURL }] : []),
      ...(url ? [] : [{ title: 'Duplicate', onClick: duplicateItem }]),
      { title: 'Rename', onClick: renameItem },
      { title: 'Delete', onClick: deleteItem },
      ...(!url
        ? [
            {
              title: 'Download',
              onClick: download,
              customClasses: [
                'border-top',
                isPreparingDownload ? 'button--add loading' : '',
              ],
            },
          ]
        : []),
    ];

    return (
      <>
        {menuItems.map(({ title, onClick, customClasses }, index) => {
          return (
            <button
              className={clsx(
                'button--unstyled',
                'button--menu',
                customClasses,
              )}
              onClick={onClick}
              key={index}
            >
              {title}
            </button>
          );
        })}

        <div className={clsx('ap-dropdown__menu-section', 'border-top')}>
          <FormSelect
            onChange={restrictionOnChange}
            emptyValue="None"
            value={restriction}
            name="Restrictions"
            className="note-restriction-dropdown"
            scrollLockOnOpen={false}
          >
            {NOTE_RESTRICTIONS.map(item => renderOption(item.value,item.label))}
          </FormSelect>
        </div>
      </>
    );
  };

  const trigger = (
    <DropdownButton className="button--unstyled button--options">
      <span className="fa fa-ellipsis-v" />
    </DropdownButton>
  );

  return (
    <>
      <DefaultSm>
        <APDropdown
          isOpen={opened}
          show={(e) => {
            setOpened(true);
            setTarget(e.currentTarget.parentNode.parentNode);
            e.currentTarget.parentNode.parentNode.classList.remove(
              'hover-expose',
            );
          }}
          dismiss={() => {
            setOpened(false);
            setTarget(null);
            if (isDesktop) {
              target.classList.add('hover-expose');
            }
          }}
          triggerButton={trigger}
          backdrop={false}
          scrollLockOnOpen={false}
        >
          {renderOptions()}
        </APDropdown>
      </DefaultSm>
      <MobileSm>
        <Drawer
          isOpen={opened}
          show={() => setOpened(true)}
          dismiss={dismiss}
          position="bottom"
          triggerButton={trigger}
        >
          {renderOptions()}
        </Drawer>
      </MobileSm>
      {deleteConfirmOpened && (
        <DeleteTemplateModal
          opened={deleteConfirmOpened}
          setOpened={setDeleteConfirmOpened}
          id={id}
          isShortcut={noteTemplate?.isShortcut}
          deleteNoteTemplate={deleteNoteTemplate}
        />
      )}
      {imageCropperOpened && (
        <CoverImageModal
          image={image}
          imageUrls={imageUrls}
          originalImage={originalImage}
          setImageUpdating={setImageUpdating}
          openImageCropper={imageCropperOpened}
          setImageCropperOpen={setImageCropperOpen}
          updateNoteTemplate={updateNoteTemplate}
          noteTemplate={noteTemplate}
        />
      )}
      {urlModalOpened && (
        <URLModal
          urlModalOpened={urlModalOpened}
          setUrlModalOpened={setUrlModalOpened}
          url={url}
          updateUrl={updateTemplateUrl}
        />
      )}
      {renameModalOpened && (
        <RenameTemplateModal
          opened={renameModalOpened}
          setOpened={setRenameModalOpened}
          title={noteTemplate.title}
          renameNoteTemplate={renameTemplate}
        />
      )}
      {errorModalOpened && (
        <APModal
          open={errorModalOpened}
          width={620}
          height={300}
          onClose={errorModalCloseHandler}
          className="ap-modal--note-options-confirmation"
          size="narrow"
          title={errorModalTitle}
          actions={(
            <button
              onClick={errorModalCloseHandler}
              className="button"
            >
              {errorModalButtonTitle}
            </button>
          )}
        >
          {errorModalMessage}
        </APModal>
      )}
    </>
  );
};

export const DeleteTemplateModal = ({
  opened,
  setOpened,
  id,
  isShortcut,
  deleteNoteTemplate,
  isTemplate = true,
  actionUrl = null,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [hasError, setHasError] = useState(false);
  const deleteComplete = useSelector((state) => state.projectTools.note.deleteComplete);

  const onSave = () => {
    if (confirmText === 'DELETE') {
      deleteNoteTemplate(id);
    } else {
      setHasError(true);
      setError('Please enter text as displayed to confirm.');
    }
  };

  // Wait for delete action to complete before redirectng.
  useEffect(() => {
    if (actionUrl && deleteComplete) window.location.href = actionUrl;
  }, [deleteComplete]);

  useEffect(() => {
    setHasError(false);
    if (confirmText) {
      setError('');
    }
  }, [confirmText]);

  const onCancel = () => {
    setOpened(false);
  };

  const onChange = (event) => {
    setConfirmText(event.target.value);
  };

  return (
    <APModal
      open={opened}
      width={350}
      height={175}
      onClose={onCancel}
      name="delete-note-template-confirmation"
      className="ap-modal--delete-confirmation"
      size="xs"
      hasHeader={false}
      actions={(
        <>
          <button
            type="button"
            className="button-cancel button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button-delete button"
            onClick={onSave}
          >
            Delete Item
          </button>
        </>
      )}
    >
      {isTemplate ? (
        <p>Deleting this {isShortcut ? 'shortcut' : 'note'} template will permanently remove it from your templates,
          but won’t affect existing {isShortcut ? 'shortcuts' : 'notes'} in projects. Type “DELETE” to confirm.</p>
      ) : (
        <p>Deleting this {isShortcut ? 'shortcut' : 'note'} will permanently remove it from your notebook.
          Type “DELETE” to confirm.</p>
      )}
      <InputField
        name="delete_confirmation"
        className="ap-modal__delete-confirmation"
        value={confirmText}
        onChange={onChange}
        visuallyHidden
        showError={hasError}
      />
      <section className='note-error'>
        {error}
      </section>
    </APModal>
  );
};

const RenameTemplateModal = ({ opened, setOpened, title, renameNoteTemplate }) => {
  const [noteTemplateTitle, setNoteTemplateTitle] = useState(title);
  const [error, setError] = useState('');

  useEffect(() => {
    if (noteTemplateTitle) {
      setError('');
    }
  }, [noteTemplateTitle]);

  const onSave = () => {
    if (noteTemplateTitle) {
      renameNoteTemplate(noteTemplateTitle);
      setOpened(false);
    } else {
      setError('Please fill the note template title.');
    }
  };

  const onCancel = () => {
    setOpened(false);
  };

  const onChange = (event) => {
    setNoteTemplateTitle(event.target.value);
  };

  const modalActions = [{ onClick: onSave, title: 'Save' }];

  const modalContent = (
    <InputField
      name="note_template_title"
      label="Title"
      defaultValue={noteTemplateTitle}
      onChange={onChange}
      onPointerDown={(e) => e.stopPropagation()}
      showError={!noteTemplateTitle}
    />
  );

  return renderModal(
    opened,
    'Edit your title',
    'edit-note-template-title',
    modalActions,
    onCancel,
    modalContent,
    error,
  );
};

const URLModal = ({ urlModalOpened, setUrlModalOpened, url, updateUrl }) => {
  const [shortcutUrl, setShortcutUrl] = useState(url);
  const [isShortcutUrlValid, setShortcutUrlValid] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isShortcutUrlValid) {
      setError('');
    }
  }, [isShortcutUrlValid]);

  const onSave = () => {
    if (shortcutUrl && isShortcutUrlValid) {
      updateUrl(shortcutUrl);
      setUrlModalOpened(false);
    } else {
      setError('Please review and update your website format to continue.');
    }
  };

  const onCancel = () => {
    setUrlModalOpened(false);
  };

  const onChange = (event) => {
    setShortcutUrl(event.target.value);
    setShortcutUrlValid(isUrlValid(event.target.value));
  };

  const modalActions = [{ onClick: onSave, title: 'Save' }];

  const modalContent = (
    <InputField
      name="note_shortcut"
      label="Shortcut"
      value={shortcutUrl}
      onChange={onChange}
      onPointerDown={(e) => e.stopPropagation()}
      onBlur={() => {
        if (shortcutUrl && !shortcutUrl.includes('https://')) {
          setShortcutUrl(`https://${shortcutUrl}`);
        }
      }}
      showError={!isShortcutUrlValid}
    />
  );

  return renderModal(
    urlModalOpened,
    'Edit your URL.',
    'edit-note-template-url',
    modalActions,
    onCancel,
    modalContent,
    error,
  );
};

const renderOption = (value, label, restProps) => {
  return (
    <option
      key={`move-to-${value}`}
      value={value}
      {...restProps}
    >
      {label}
    </option>
  );
};

const renderModal = (
  modalOpened,
  title,
  name,
  modalActions,
  onClose,
  modalContent,
  error,
) => {

  const renderModalActions = () => {
    return (
      <>
        {modalActions.map((item, index) => (
          <button
            type="button"
            onClick={item.onClick}
            key={index}
            className={item.className ?? 'button'}
          >
            {item.title}
          </button>
        ))}
      </>
    );
  };

  return (
    <APModal
      open={modalOpened}
      title={title}
      name={name}
      onClose={onClose}
      actions={renderModalActions()}
    >
      {modalContent}
      <section className='note-error'>
        {error}
      </section>
    </APModal>
  );
};

const CoverImageModal = ({ image, imageUrls, originalImage, setImageUpdating,
  openImageCropper, setImageCropperOpen, noteTemplate, updateNoteTemplate }) => {
  const [imageKey, setImageKey] = useState(image?.key);
  const [imageUrl, setImageUrl] = useState(imageUrls?.original?.url);
  const [originalImageKey, setOriginalImageKey] = useState(originalImage?.key);

  const updateImageKey = (newImageKey) => {
    if (!newImageKey) {
      updateNoteTemplate({ id: noteTemplate.id, noteBody: { ...noteTemplate, imageKey: 'delete' } });
      setImageUpdating(false);
    }
    else if (imageKey !== newImageKey) {
      updateNoteTemplate({ id: noteTemplate.id, noteBody: { ...noteTemplate, imageKey: newImageKey } });
      setImageUpdating(false);
    };
    setImageKey(newImageKey);
  };

  return (
    <ImageUploadField
      label='Cover image'
      imageUploadType='shortcut'
      imageUrl={imageUrl}
      originalImageKey={originalImageKey}
      updateImageKey={updateImageKey}
      updateImageUrl={setImageUrl}
      updateOriginalImageKey={setOriginalImageKey}
      imageInfoPath='wedding/note/images'
      imageProcessPath='wedding/note/image'
      setImageUpdating={setImageUpdating}
      cropperOnly
      openCropper={openImageCropper}
      setImageModalOpen={setImageCropperOpen}
      imageCropperUploadText='Drag your image here'
      cropperConfig={image?.cropPosition}
    />
  );
};
